// frontend/src/services/PollingManager.js

class PollingManager {
    constructor() {
        this.activePolls = new Map(); // Track all active polls
        this.pageIntervals = new Map(); // Page-specific intervals
        this.visibilityListeners = new Map(); // Track visibility listeners
        this.isDocumentVisible = !document.hidden;

        // Default intervals (milliseconds)
        this.defaultIntervals = {
            projects: 15000,      // 15 seconds
            tasks: 10000,         // 10 seconds
            comments: 8000,       // 8 seconds
            notifications: 12000, // 12 seconds
            calendar: 30000,      // 30 seconds
            sprints: 10000,       // 20 seconds
            backlog: 15000,       // 15 seconds
            'kpi-approvals': 30000, // 30 seconds
            default: 10000,
            'task-history': 60000,        // 10 seconds fallback
        };

        // Initialize visibility listener
        this.initVisibilityListener();
    }

    advanceLastCheck(key) {
        const poll = this.activePolls.get(key);
        if (poll) {
        poll.lastExecuted = 0; // Reset to force immediate execution
        this.executePoll(poll);
        console.log(`[PollingManager] Advanced check for ${key}`);
        return true;
        }
        return false;
    }

    // Initialize document visibility listener
    initVisibilityListener() {
        document.addEventListener('visibilitychange', () => {
            this.isDocumentVisible = !document.hidden;

            if (this.isDocumentVisible) {
                // Resume all paused polls
                this.resumeAllPolls();
            } else {
                // Pause all active polls
                this.pauseAllPolls();
            }
        });
    }

    // Set custom interval for a specific page/key
    setInterval(key, milliseconds) {
        this.pageIntervals.set(key, milliseconds);
    }

    // Get interval for a key (custom or default)
    getInterval(key) {
        return this.pageIntervals.get(key) ||
            this.defaultIntervals[key] ||
            this.defaultIntervals.default;
    }

    // Start polling for a key
    start(key, callback, params = {},  customInterval = null) {
        // If already polling, don't start again
        if (this.activePolls.has(key)) {
            console.log(`[PollingManager] ${key} already polling`);
            return key;
        }

        const interval = customInterval || this.getInterval(key);

        const poll = {
            key,
            callback,
            params,
            interval,
            timer: null,
            paused: false,
            lastExecuted: Date.now()
        };

        // Start immediately
        this.executePoll(poll);

        // Set interval
        poll.timer = setInterval(() => {
            if (!poll.paused && this.isDocumentVisible) {
                this.executePoll(poll);
            }
        }, interval);

        this.activePolls.set(key, poll);
        console.log(`[PollingManager] Started ${key} (interval: ${interval}ms)`);

        return key;
    }

    // Execute poll callback
    async executePoll(poll) {
        try {
            poll.lastExecuted = Date.now();
            await poll.callback();
        } catch (error) {
            console.error(`[PollingManager] Poll error for ${poll.key}:`, error);
        }
    }

    // Stop polling for a key
    stop(key) {
        const poll = this.activePolls.get(key);
        if (poll) {
            if (poll.timer) {
                clearInterval(poll.timer);
            }
            this.activePolls.delete(key);
            console.log(`[PollingManager] Stopped ${key}`);
        }
    }

    // Pause polling (without clearing timer)
    pause(key) {
        const poll = this.activePolls.get(key);
        if (poll) {
            poll.paused = true;
            console.log(`[PollingManager] Paused ${key}`);
        }
    }

    // Resume polling
    resume(key) {
        const poll = this.activePolls.get(key);
        if (poll) {
            poll.paused = false;
            if (this.isDocumentVisible) {
                this.executePoll(poll);
            }
            console.log(`[PollingManager] Resumed ${key}`);
        }
    }

    // Pause all polls
    pauseAllPolls() {
        this.activePolls.forEach((poll, key) => {
            this.pause(key);
        });
        console.log('[PollingManager] All polls paused (document hidden)');
    }

    // Resume all polls
    resumeAllPolls() {
        this.activePolls.forEach((poll, key) => {
            this.resume(key);
        });
        console.log('[PollingManager] All polls resumed (document visible)');
    }

    // Stop all polls
    stopAll() {
        this.activePolls.forEach((poll, key) => {
            this.stop(key);
        });
        console.log('[PollingManager] All polls stopped');
    }

    // Get active poll count
    getActivePollCount() {
        return this.activePolls.size;
    }

    // Get poll status
    getPollStatus(key) {
        const poll = this.activePolls.get(key);
        if (!poll) return { active: false };

        return {
            active: true,
            paused: poll.paused,
            interval: poll.interval,
            lastExecuted: poll.lastExecuted,
            timeSinceLast: Date.now() - poll.lastExecuted
        };
    }

    // Configure page-specific intervals (call this in App.js or main config)
    configurePageIntervals(config) {
        Object.entries(config).forEach(([key, interval]) => {
            this.setInterval(key, interval);
        });
        console.log('[PollingManager] Intervals configured:', config);
    }
}

// Export singleton instance
export const pollingManager = new PollingManager();
export default pollingManager;